import { Dispatch } from "@reduxjs/toolkit";
import { useAppDispatch, useAppSelector } from "./hooks";
import { ReadableStateKey, SetAction, State, UpdateAction } from "./store";

export const useSharedState = <T extends keyof State>(key: T) => {
  const writeOnly = useSharedStateWriteOnly(key);
  return [useSharedStateReadOnly(key)[0], writeOnly[1], writeOnly[2]] as const;
};

export const useSharedStateReadOnly = <T extends ReadableStateKey>(key: T) => {
  const value = useAppSelector((state) => state[key]);
  return [value] as const;
};

export const useSharedStateWriteOnly = <T extends keyof State>(key: T) => {
  const dispatch = useAppDispatch() as Dispatch<SetAction<T>>;
  const setValue = (newValue: State[T]) => {
    dispatch({
      type: "set",
      key: key,
      value: newValue,
    });
  };
  return [undefined, setValue, useSharedStateWriteOnlyUpdater(key)[2]] as const;
};

export const useSharedStateWriteOnlyUpdater = <T extends keyof State>(
  key: T
) => {
  const dispatch = useAppDispatch() as Dispatch<UpdateAction<T>>;
  const updateValue = (updater: (current: State[T]) => State[T]) => {
    dispatch({
      type: "update",
      key: key,
      updater,
    });
  };
  return [undefined, undefined, updateValue] as const;
};
